import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button, Modal} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import {Tab} from 'react-bootstrap';
import Nav from "react-bootstrap/Nav";
import {Loader} from "../components";
import styled from "styled-components";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faTimes } from '@fortawesome/pro-solid-svg-icons'


const StyledLoader = styled.div`
  width: 100%;
  height: 100%;
  .spinner-wrapper {
    height: 100%;
    display: flex;
    .spinner {
      display: flex;
    }
  }
`

const DevicePreview = ({content, mobileInterfaceCallback, isLoading, isSuccess, showSettings, input}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [transformCss, setTransformCss] = useState({})

  const deviceRef = useRef();

  const iFrameRef = useRef(null)

  const setMobileInterface = () => {
    iFrameRef.current.contentWindow.mobileInterface = {
      onSave: mobileInterfaceCallback,
      onAccept: mobileInterfaceCallback,
      onRefuse: mobileInterfaceCallback,
      onCancel: mobileInterfaceCallback
    }
  }

  const setIFrameRef = useCallback(node => {
    if (iFrameRef.current) {
      iFrameRef.current.removeEventListener('load', setMobileInterface)
    }

    if (node) {
      node.addEventListener('load', setMobileInterface)
    }

    iFrameRef.current = node
  }, [])

  const loaderProps = { success: (!isLoading && isSuccess), error: (!isLoading && !isSuccess) }

  const updateWidthAndHeight = () => {
    setWindowWidth(window.innerWidth);
    setWindowHeight(window.innerHeight);
  };

  useEffect(() => {
    window.addEventListener("resize", updateWidthAndHeight);
    return () => window.removeEventListener("resize", updateWidthAndHeight);
  }, []);

  useEffect(() => {
    const margin = 0.9
    const deviceWidth = deviceRef.current.offsetWidth
    const deviceHeight = deviceRef.current.offsetHeight

    if( deviceWidth > (windowWidth * margin) || deviceHeight > (windowHeight * margin)) {
      const scale = Math.min((windowWidth * margin)/deviceWidth, (windowHeight * margin)/deviceHeight)
      setTransformCss({transform: `scale(${scale})`})
    } else {
      setTransformCss({})
    }
  }, [windowWidth, windowHeight])

  // useEffect(() => {
  //   if (iFrameRef?.current) {
  //      iFrameRef.current.contentWindow.setupSettings(showSettings ? JSON.parse(input) : undefined)
  //   }
  // }, [showSettings, iFrameRef])

  return (
    <div className="device device-iphone-8 device-gold" style={transformCss} ref={deviceRef}>
      <div className="device-frame">
        <Loader {...loaderProps} as={StyledLoader} >
          <iframe className="device-content" title='showcase' srcDoc={content} ref={setIFrameRef} style={{
            width: '100%',
            height: '100%',
            margin: 0
          }}/>
        </Loader>
      </div>
      <div className="device-stripe"></div>
      <div className="device-header"></div>
      <div className="device-sensors"></div>
      <div className="device-btns"></div>
      <div className="device-power"></div>
    </div>
  )
}

const PreviewOutput = ({consentString, decodedConsentString, consentsList, actions, extraOptions}) => {
  const { t } = useTranslation()

  const ListItem = ({consent, name}) => {
    return <li className={`list-group-item ${consent ? 'list-group-item-success' : ''}`}>
      {consent && <FontAwesomeIcon icon={faToggleOn} /> }
      {name}
    </li>
  }

  return (
    <div className="preview-misc">
      <div className="preview-misc-element preview-output">
        {extraOptions}
        <div className="title">
          {t('cmp.preview.output')}
        </div>
        <div className="preview-output-content">
          <p className="poc-info"><b>{t('cmp.preview.action')}</b> <span className="poc-last-action">{actions?.length > 0 ? t('cmp.preview.'+actions.reverse()[0].action) : '-'}</span></p>
          <p className="poc-info"><b>{t('cmp.preview.iabString')}</b> <span className="poc-iab-string">{consentString}</span></p>
          <Tab.Container id="preview-source" defaultActiveKey="singlespot">
            <Nav className="poc-results" id="poc-results-tabs">
              <Nav.Link className="poc-tab" eventKey="singlespot" as={"button"}>{t('cmp.preview.singlespot')}</Nav.Link>
              <Nav.Link className="poc-tab" eventKey="iab" as={"button"}>{t('cmp.preview.iab')}</Nav.Link>
            </Nav>
            <Tab.Content id="poc-results-tabs-content">
              <Tab.Pane eventKey="singlespot">
                <Tab.Container id="preview-data" defaultActiveKey="purposes">
                  <Nav id="poc-singlespot-tabs">
                    <Nav.Link className="poc-tab" eventKey="purposes" as={"button"}>{t('cmp.preview.purposes')}</Nav.Link>
                    <Nav.Link className="poc-tab" eventKey="vendors" as={"button"}>{t('cmp.preview.vendors')}</Nav.Link>
                  </Nav>
                  <Tab.Content id="poc-singlespot-tabs-content">
                    <Tab.Pane eventKey="purposes">
                      <ul id="poc-purposes" className="list-group poc-tab-content active">
                        {!consentsList?.purposes?.length > 0 && <li className="list-group-item">{t('cmp.preview.nothingToDisplay')}</li>}
                        {consentsList?.purposes?.flatMap(purpose => {
                          if (purpose.mapped_purposes) {
                            purpose.mapped_purposes.map(mapped_purpose => {
                              return <ListItem key={mapped_purpose.title} consent={mapped_purpose.consent} name={mapped_purpose.title}/>
                            })
                          } else {
                            return <ListItem key={purpose.title} consent={purpose.consent} name={purpose.title}/>
                          }
                        })}
                      </ul>
                    </Tab.Pane>
                    <Tab.Pane eventKey="vendors">
                      <ul id="poc-vendors" className="list-group poc-tab-content">
                        {!consentsList?.vendors?.length > 0 && <li className="list-group-item">{t('cmp.preview.nothingToDisplay')}</li>}
                        {consentsList?.vendors?.flatMap(vendor => <ListItem key={vendor.name} name={vendor.name} consent={vendor.consent} />)}
                      </ul>
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </Tab.Pane>
              <Tab.Pane eventKey="iab">
                <div id="poc-results-iab" className="poc-tab-content">
                  <p className="poc-info"><b>{t('cmp.preview.allowedPurposeIds')}</b> <span className="poc-iab-allowed-purpose-ids">{decodedConsentString.allowedPurposeIds?.join(', ') || '-'}</span></p>
                  <p className="poc-info"><b>{t('cmp.preview.allowedVendorIds')}</b> <span className="poc-iab-allowed-vendor-ids">{decodedConsentString.allowedVendorIds?.join(', ') || '-'}</span></p>
                  <p className="poc-info"><b>{t('cmp.preview.specialFeatureIds')}</b> <span className="poc-iab-allowed-special-feature-ids">{decodedConsentString.allowedSpecialFeatureIds?.join(', ') || '-'}</span></p>
                  <p className="poc-info"><b>{t('cmp.preview.legIntPurposeIds')}</b> <span className="poc-iab-allowed-leg-int-purpose-ids">{decodedConsentString.allowedLegIntPurposeIds?.join(', ') || '-'}</span></p>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      </div>
    </div>
  )
}

const CustomDialog = ({handleClose,
                      content,
                      setConsentString: setRawConsentString,
                      decodedConsentString,
                      isLoading,
                      isSuccess,
                      extraOptions,
                      showSettings,
                      input}) => {

  const [consentString, setConsentString] = useState()
  const [consentsList, setConsentsList] = useState()
  const [actions, setActions] = useState()

  const mobileInterfaceCallback = (consentsList, consentString, actions) => {
    consentsList && setConsentsList(JSON.parse(consentsList))
    actions && setActions(JSON.parse(actions))
    setConsentString(consentString)
  }

  useEffect(() => {
    setRawConsentString(consentString)
  }, [consentString])

  return (
    <div id="preview-device">
      <div className="preview-container">
        <DevicePreview
          content={content}
          mobileInterfaceCallback={mobileInterfaceCallback}
          isLoading={isLoading}
          isSuccess={isSuccess}
          showSettings={showSettings}
          input={input}/>
        <Button className="preview-device-dismiss" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} />
        </Button>
        <PreviewOutput consentString={consentString} decodedConsentString={decodedConsentString} consentsList={consentsList} actions={actions} extraOptions={extraOptions}/>
      </div>
    </div>
  )
}

export const CmpPreview = ({show,
                           handleClose,
                           preview,
                           setConsentString,
                           decodedConsentString,
                           isAppPreviewLoading,
                           isAppPreviewSuccess,
                           children,
                           showSettings}) => {

  return <Modal show={show}
                onHide={handleClose}
                dialogAs={CustomDialog}
                handleClose={handleClose}
                content={preview.content}
                input={preview.input}
                setConsentString={setConsentString}
                decodedConsentString={decodedConsentString}
                isLoading={isAppPreviewLoading}
                isSuccess={isAppPreviewSuccess}
                extraOptions={children}
                showSettings={showSettings}/>

}
